<template>
      <v-dialog
        transition="dialog-bottom-transition"
        :max-width="custumWidth"
        :value="value"
        persistent
      >
          <v-card ref="popUpCard">
            <v-toolbar v-if="enableFlashTypeMsg"
             :color="flashType" dark>{{ textByColor }}</v-toolbar>
            <v-card-text class="pa-0 pl-4 pt-5">
              <slot name="customsLabelOrInfo"/>
            </v-card-text>
            <v-card-actions class="justify-end">
              <slot name="customsButtonsForPopUp"/>
            </v-card-actions>
          </v-card>
      </v-dialog>
</template>

<script>
import '../../../assets/style.css';
import { isEnter } from '../../../util/shared/vue-global';

// v-dialog detaches its content and focuses the content wrapper itself as it opens, so we
// have to wait out the transition before taking focus or Vuetify hands it straight back.
const DEFAULT_BTN_FOCUS_DELAY_MS = 300;

export default {
  name: 'CommonPopUp',
  data() {
    return {
      enterGuardActive: false,
      enterGuardTimerId: null,
      focusTimerId: null,
    };
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    custumWidth: {
      type: Number,
      default: 600,
    },
    enableFlashTypeMsg: {
      type: Boolean,
      default: true,
    },
    flashType: {
      type: String,
      default: 'info', // info, warning, error
    },
    // Opt-in (off by default so the existing pop-ups keep their window-listener behaviour).
    // When on, the button marked `data-default-btn` in the customsButtonsForPopUp slot gets
    // focus once the dialog has opened, so Enter alone activates it - no Tab first. Opting in
    // also stops the `onEnter` emit, since the focused button is what Enter hits now and
    // firing both would run the handler twice.
    focusDefaultBtn: {
      type: Boolean,
      default: false,
    },
    // Enter arriving in this window after the pop-up opens is dropped: the keystroke that
    // triggered the lookup, or a barcode scanner's trailing Enter, must not dismiss the
    // warning before the clerk has had a chance to read it. Only applies with focusDefaultBtn.
    enterGuardMs: {
      type: Number,
      default: 500,
    },
  },
  created() {
    window.addEventListener('keydown', this.handleSelShortCut);
    // Capture phase, so a guarded Enter is dropped before it ever reaches the focused button.
    window.addEventListener('keydown', this.swallowGuardedEnter, true);
    if (this.value) this.onPopUpOpened();
  },
  destroyed() {
    window.removeEventListener('keydown', this.handleSelShortCut);
    window.removeEventListener('keydown', this.swallowGuardedEnter, true);
    clearTimeout(this.enterGuardTimerId);
    clearTimeout(this.focusTimerId);
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.onPopUpOpened();
      else this.clearEnterGuard();
    },
  },
  computed: {
    textByColor() {
      if (this.flashType === 'info') return 'Info !';
      if (this.flashType === 'warning') return 'Warning !';
      if (this.flashType === 'error') return 'Error !';
      return '';
    },
    backColor() {
      if (this.flashType === 'info') return 'primary';
      if (this.flashType === 'warning') return 'amber accent-4';
      if (this.flashType === 'error') return 'red';
      return '';
    },
  },
  methods: {
    onPopUpOpened() {
      if (!this.focusDefaultBtn) return;
      clearTimeout(this.enterGuardTimerId);
      this.enterGuardActive = true;
      this.enterGuardTimerId = setTimeout(() => {
        this.enterGuardActive = false;
      }, this.enterGuardMs);
      this.focusDefaultButton();
    },
    clearEnterGuard() {
      clearTimeout(this.enterGuardTimerId);
      this.enterGuardActive = false;
    },
    focusDefaultButton() {
      clearTimeout(this.focusTimerId);
      this.focusTimerId = setTimeout(() => {
        this.$nextTick(() => {
          const card = this.$refs.popUpCard;
          const btnEl = card && card.$el && card.$el.querySelector('[data-default-btn]');
          if (btnEl) btnEl.focus();
        });
      }, DEFAULT_BTN_FOCUS_DELAY_MS);
    },
    swallowGuardedEnter(e) {
      if (!this.focusDefaultBtn || !this.enterGuardActive) return;
      if (!isEnter(e)) return;
      e.preventDefault();
      e.stopPropagation();
    },
    handleSelShortCut(e) {
      if (this.focusDefaultBtn) return;
      const isItEnter = isEnter(e);
      if (isItEnter) this.$emit('onEnter');
    },
  },
};
</script>
